/**
 * Centralized API client for all API calls
 * Handles CORS, authentication, error handling, and common configurations
 */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { getApiGatewayUrl } from '../utils/environmentDetection';

const API_GATEWAY_URL = getApiGatewayUrl();
const API_TIMEOUT = parseInt(process.env.API_TIMEOUT!) || 10000;

/**
 * Standard API request interface that includes common request parameters
 */
export interface ApiRequest {
  warehouseId?: string;
  userId?: string;
  visitReason?: string;
  locale?: string;
  [key: string]: any;
}

/**
 * Standard API response wrapper
 */
export interface ApiResponse<T = any> {
  data: T;
  success: boolean;
  message?: string;
  errorCode?: string;
}

/**
 * Common API error interface
 */
export interface ApiError {
  message: string;
  code?: string;
  status?: number;
  details?: any;
}

/**
 * Create the main API client instance
 */
class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_GATEWAY_URL,
      timeout: API_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Access-Control-Allow-Headers': 'Origin, X-Requested-With, Content-Type, Accept, Authorization',
      },
      // Enable credentials if needed for authentication
      withCredentials: false,
    });
  }

  /**
   * Make a GET request
   */
  async get<T = any>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(endpoint, config);
    return response.data;
  }

  /**
   * Make a POST request
   */
  async post<T = any>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(endpoint, data, config);
    return response.data;
  }

  /**
   * Make a PUT request
   */
  async put<T = any>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(endpoint, data, config);
    return response.data;
  }

  /**
   * Make a DELETE request
   */
  async delete<T = any>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(endpoint, config);
    return response.data;
  }

  /**
   * Make a PATCH request
   */
  async patch<T = any>(endpoint: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(endpoint, data, config);
    return response.data;
  }

  /**
   * Get the underlying axios instance for advanced usage
   */
  getClient(): AxiosInstance {
    return this.client;
  }

  /**
   * Update configuration (e.g., for authentication tokens)
   */
  updateConfig(config: Partial<AxiosRequestConfig>): void {
    Object.assign(this.client.defaults, config);
  }

  /**
   * Add authorization header
   */
  setAuthToken(token: string): void {
    this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  /**
   * Remove authorization header
   */
  removeAuthToken(): void {
    delete this.client.defaults.headers.common['Authorization'];
  }
}

// Create and export a singleton instance
export const apiClient = new ApiClient();

// Export the class for testing or advanced usage
export { ApiClient };
